import { setRestVerb } from "store/reducers/generalActions";
import { AppThunk } from "store";
import { api } from "api/api";
import { RuleTable, RuleTableResponse } from "types/table";
import { GetTableSummariesRequest } from "types/tableSummary";
import { TableAsyncStatus } from "./tables.types";
import { toast } from "react-toastify";
import cloneDeep from "lodash/cloneDeep";
import { ObjectAssignedEnum } from "types/validation";
import { reorder } from "components/Table/helper";
import { countRuleTableValidationsBySeverity } from "./tableSummaries.helpers";
import {
  formatValidationResultToastMessage,
  totalValidationIssueCount,
} from "../validation/validationCounts.helpers";

export const SET_NEW_TABLE = "products/tables/SET_NEW_TABLE";
export const ADD_NEW_TABLE = "products/tables/ADD_NEW_TABLE";
export const DELETE_TABLE = "products/tables/DELETE_TABLE";
export const SET_PL_TABLES = "products/tables/SET_PL_TABLES";
export const SET_PL_TABLES_DETAILS = "products/tables/SET_PL_TABLES_DETAILS";
export const EDIT_TABLE = "products/tables/EDIT_TABLE";
export const SET_TABLE = "products/tables/SET_TABLE";
export const DELETE_TABLE_ROW = "products/tables/DELETE_TABLE_ROW";
export const ADD_NEW_PROPERTY = "products/tables/ADD_NEW_PROPERTY";
export const SET_VERB = "products/tables/REST_VERB";
export const SET_TABLES_INITIAL_STATE =
  "products/tables/SET_TABLES_INITIAL_STATE";
export const GET_TABLE_VALIDATION_BY_OBJECT_ID =
  "products/tables/GET_TABLE_VALIDATION_BY_OBJECT_ID";
export const SET_PL_TABLES_PAGINATION =
  "products/tables/SET_PL_TABLES_PAGINATION";
export const SET_TABLE_SUMMARIES = "products/tables/SET_TABLE_SUMMARIES";
export const SET_TABLE_LOAD_STATUS = "products/tables/SET_TABLE_LOAD_STATUS";
export const CLEAR_CACHED_TABLES = "products/tables/CLEAR_CACHED_TABLES";

export const TABLES_ITEMS_PER_PAGE = 5;

export const setNewTable = (newTable: RuleTable) => {
  return {
    type: SET_NEW_TABLE,
    payload: newTable,
  };
};

export const addTableToProductlineAction = (table: RuleTable) => {
  return async (dispatch, getState) => {
    dispatch(setRestVerb("products/tables", true, "posting"));

    try {
      const { tables, selectedProductLine } = getState().products;

      const { data } = await api.createProductLineRulesTable(
        table,
        selectedProductLine.id
      );
      const newById = { [data.id]: data, ...tables.byId };
      const newAllIds = tables.allIds.concat(data.id);

      dispatch({
        type: ADD_NEW_TABLE,
        payload: {
          byId: newById,
          allIds: newAllIds,
          total: tables.total,
        },
      });
      setTimeout(
        () =>
          window.scrollTo({
            top: document.body.scrollHeight,
            left: 0,
            behavior: "smooth",
          }),
        300
      );
    } catch (e) {
      console.error(e, "error while adding a rule table to product line");
    }
    dispatch(setRestVerb("products/tables", false, "posting"));
  };
};

export const deleteTable = (tableId) => {
  return async (dispatch, getState) => {
    dispatch(setRestVerb("products/tables", true, "deleting"));

    try {
      const { selectedProductLine } = getState().products;
      const response = await api.deleteRuleTable(
        selectedProductLine.id,
        tableId
      );
      if (response && response.status === 204) {
        dispatch({
          type: DELETE_TABLE,
          payload: { id: tableId },
        });
      }
    } catch (e) {
      console.error(e, "error while deleting rule table");
    }
    dispatch(setRestVerb("products/tables", false, "deleting"));
  };
};

export const getTablesAction = ({
  tableTypes,
  rulesetIds,
  propertyIds,
  productLineId,
  items = TABLES_ITEMS_PER_PAGE,
  page = 0,
}: {
  tableTypes?: any;
  rulesetIds?: any;
  propertyIds?: any;
  productLineId: string;
  items?: number;
  page?: number;
}) => {
  return async (dispatch) => {
    dispatch(setRestVerb("products/tables", true, "getting"));
    try {
      const { data } = await api.getRuleTables(
        productLineId,
        items,
        page,
        tableTypes,
        rulesetIds,
        propertyIds
      );
      const newAllIds: Array<any> = [];
      const newById = {};

      if (!data.ruleTables) return;
      data.ruleTables.map((table) => {
        newAllIds.push(table.id);
        newById[table.id] = table;
      });

      dispatch({
        type: SET_PL_TABLES,
        payload: {
          byId: newById,
          allIds: newAllIds,
          page,
          total: data.total,
        },
      });
    } catch (e) {
      console.error(e, "error while getting all rules tables to product line");
    }
    dispatch(setRestVerb("products/tables", false, "getting"));
  };
};

export const addTableToStoreAction = ({
  table,
}: {
  table: RuleTableResponse;
}) => {
  return async (dispatch, getState) => {
    if (getState().products?.selectedProductLine) {
      const { tables } = getState().products;

      try {
        const newById = { ...tables.byId, [table.id]: table };
        const newAllIds = tables.allIds.concat(table.id);

        dispatch({
          type: ADD_NEW_TABLE,
          payload: {
            byId: newById,
            allIds: newAllIds,
          },
        });
      } catch (e) {
        console.error(e, "Error while adding table to redux store");
        toast.error("Failed to add table to redux store");
      }
    }
  };
};

export const getTableAction = ({ tableId }: { tableId: string }) => {
  return async (dispatch, getState) => {
    dispatch(setRestVerb("products/tables", true, "getting"));
    if (getState().products?.selectedProductLine) {
      const { id: productLineId } = getState().products.selectedProductLine;
      const { tables } = getState().products;

      try {
        const { data } = await api.getRuleTable(productLineId, tableId);

        const newById = { ...tables.byId, [data.id]: data };
        const newAllIds = tables.allIds.concat(data.id);

        dispatch({
          type: ADD_NEW_TABLE,
          payload: {
            byId: newById,
            allIds: newAllIds,
            total: tables.total,
          },
        });
      } catch (e) {
        console.error(e, "error while getting ruletable");
        toast.error("Failed to load rule table");
      }
      dispatch(setRestVerb("products/tables", false, "getting"));
    }
  };
};

export const reorderTableAction = ({
  tableId,
  targetPropertyId,
  destinationPosition,
  sourcePosition,
}) => {
  return async (dispatch, getState) => {
    const { selectedProductLine, tables } = getState().products;
    const table = { ...tables.byId[tableId] };
    const { rows } = table;
    table.rows = reorder(rows, sourcePosition, destinationPosition);

    try {
      const newByIdOptimistic = {
        ...tables.byId,
        [tableId]: table,
      };
      dispatch({
        type: EDIT_TABLE,
        payload: newByIdOptimistic,
      });
      await api.reOrderProductLineRulesTableRows(
        tableId,
        selectedProductLine.id,
        targetPropertyId,
        destinationPosition
      );
    } catch (e) {
      // optimistic rollback
      dispatch({
        type: EDIT_TABLE,
        payload: { ...tables.byId },
      });

      console.error(e);
    }
  };
};

export const editTableAction = ({
  tableId,
  updateData,
}: {
  tableId: string;
  updateData: any;
}) => {
  return async (dispatch, getState) => {
    dispatch(setRestVerb("products/tables", true, "posting"));
    const { selectedProductLine, tables } = getState().products;

    // Store a reference to the existing table data from redux store
    // used to rollback if there is any issue
    const previousTableData = tables.byId[tableId];

    try {
      //Validations

      //For a lookup table, ensure our number of lookup fields is at least 1 and no more than total number of properties
      if (Object.keys(updateData)[0] === "lookUpFieldIndex") {
        const columns = tables.byId[tableId].properties.length;
        const lookupIndex = updateData["lookUpFieldIndex"];

        if (lookupIndex > columns) {
          toast.error(
            "The number of Lookup Properties can't be greater then the number of properties in the rules table."
          );
          return;
        }

        if (lookupIndex < 1) {
          toast.error("The number of Lookup Properties can't be less then 1.");
          return;
        }
      }

      //Ensure we aren't removing all rulesets from the table.  We must have at least 1
      if ("rulesetIds" in updateData && !updateData.rulesetIds.length) {
        toast.error(
          "Unable to remove all rulesets. A table must have at least one ruleset."
        );
        throw null;
      }

      //If changing table type to LOOKUP or ATTR_LOOKUP, set the number of lookup fields to 1
      if (
        updateData.tableType &&
        ["ATTR_LOOKUP", "LOOKUP"].includes(updateData.tableType)
      ) {
        updateData.lookUpFieldIndex = "1";
      }

      //If changing table type to INCLUDE or EXCLUDE, set the number of lookup fields to 0
      if (
        updateData.tableType &&
        ["INCLUDE", "EXCLUDE"].includes(updateData.tableType)
      ) {
        updateData.lookUpFieldIndex = "0";
      }

      if (!tableId) return;

      //Call the backend to perform the update
      const res = await api.editProductLineRulesTable(
        tableId,
        selectedProductLine.id,
        updateData
      );
      if (!res) throw null;
      const { data } = res;

      //Update the table in the redux store
      dispatch({
        type: EDIT_TABLE,
        payload: {
          [data.id]: data,
        },
      });
    } catch (e) {
      console.error(e, "error while editing a rule table of a product line");

      // rollback if it fails
      dispatch({
        type: EDIT_TABLE,
        payload: { [tableId]: { ...previousTableData } },
      });
    } finally {
      dispatch(setRestVerb("products/tables", false, "posting"));
    }
  };
};

export const editTableCellAction = ({
  tableId,
  rowId,
  cellId,
  value,
}: {
  tableId: string;
  rowId: string;
  cellId: string;
  value: any;
}) => {
  return async (dispatch, getState) => {
    try {
      const { selectedProductLine, tables } = getState().products;

      const table = { ...tables.byId[tableId] };

      const { data }: any = await api.editRuleTableCell(
        selectedProductLine.id,
        tableId,
        rowId,
        cellId,
        value
      );
      const newTable = {
        ...table,
        rows: table.rows.map((row) => {
          if (row.id !== rowId) return row;
          return data;
        }),
      };

      dispatch({
        type: SET_TABLE,
        payload: { data: newTable, tableId: table.id },
      });
    } catch (e) {
      console.error(e, "error while editing a ruletable cell");
    }
  };
};

export const addPropertyRowToTable = (table: any, amount = 1) => {
  return async (dispatch, getState) => {
    dispatch(setRestVerb("products/tables", true, "posting"));
    try {
      const { selectedProductLine } = getState().products;
      const body = Array(amount).fill({
        cells: [],
      });

      const { data } = await api.addPropertiesRowToRuleTable(
        table.id,
        body,
        selectedProductLine.id
      );

      const newTable = { ...table };
      newTable.rows = newTable.rows.concat(data);

      dispatch({
        type: SET_TABLE,
        payload: { data: newTable, tableId: table.id },
      });

      toast.dismiss();
    } catch (e) {
      toast.error(e);
      console.error(e, "error while adding a new row to rule table");
    }
    dispatch(setRestVerb("products/tables", false, "posting"));
  };
};

export const deleteTableRow = (table, rowId) => {
  return async (dispatch, getState) => {
    dispatch(setRestVerb("products/tables", true, "deleting"));
    try {
      const { selectedProductLine } = getState().products;
      const response = await api.deleteRuleTableRow(
        selectedProductLine.id,
        table.id,
        rowId
      );
      const updatedTable = cloneDeep(table);

      updatedTable.rows = updatedTable.rows.filter((row) => row.id !== rowId);

      if (response && response.status === 204) {
        dispatch({
          type: DELETE_TABLE_ROW,
          payload: { table: updatedTable },
        });
      }
    } catch (e) {
      console.error(e, "error while deleting rule table");
    }
    dispatch(setRestVerb("products/tables", false, "deleting"));
  };
};

export const addNewPropertyToTableAction = (tableId, properties) => {
  return async (dispatch, getState) => {
    dispatch(setRestVerb("products/tables", true, "posting"));
    try {
      const { selectedProductLine } = getState().products;
      const response = await api.addNewPropertyToRuleTable(
        tableId,
        properties,
        selectedProductLine.id
      );
      const { data } = response;
      dispatch({
        type: ADD_NEW_PROPERTY,
        payload: { tableId: data.id, table: data },
      });
    } catch (e) {
      toast.error(e);
      console.error(e, "error while adding property rule table");
    }
    dispatch(setRestVerb("products/tables", false, "posting"));
  };
};

export const addNewPropertyPvaToTableAction = (tableId, propertyId, pvaId) => {
  return async (dispatch, getState) => {
    dispatch(setRestVerb("products/tables", true, "posting"));
    try {
      const { selectedProductLine } = getState().products;
      const { data } = await api.addNewPropertyPvaToRuleTable(
        selectedProductLine.id,
        tableId,
        propertyId,
        pvaId
      );

      dispatch({
        type: ADD_NEW_PROPERTY,
        payload: { tableId: data.id, table: data },
      });
    } catch (e) {
      toast.error(e);
      console.error(e, "error while adding property pva to rule table");
    }
    dispatch(setRestVerb("products/tables", false, "posting"));
  };
};

export const validateTable = ({ tableId }: { tableId: string }) => {
  return async (dispatch, getState) => {
    const selectedProductLineId = getState().products?.selectedProductLine?.id;

    try {
      const { data } = await api.validateRuleTable(
        selectedProductLineId,
        tableId
      );

      dispatch({
        type: GET_TABLE_VALIDATION_BY_OBJECT_ID,
        payload: { validation: data, objectId: tableId },
      });

      // Toast the result
      const counts = countRuleTableValidationsBySeverity(data, tableId);
      const message = formatValidationResultToastMessage("Table", counts);
      if (totalValidationIssueCount(counts) === 0) {
        toast.success(message);
      } else {
        toast.error(message);
      }

      return data;
    } catch (e) {
      console.error(e, `Error validating table ${tableId}`);
      toast.error(`Error validating table ${tableId}.`);
      return null;
    }
  };
};

export const getTableValidation = ({
  objectId,
  objectAssigned,
}: {
  objectId: string;
  objectAssigned: ObjectAssignedEnum;
}) => {
  return async (dispatch, getState) => {
    const selectedProductLineId = getState().products?.selectedProductLine?.id;

    try {
      const { data } = await api.getValidations(
        selectedProductLineId,
        objectId,
        objectAssigned
      );

      dispatch({
        type: GET_TABLE_VALIDATION_BY_OBJECT_ID,
        payload: { validation: data, objectId },
      });
    } catch (e) {
      console.error(e, "error while getting validation for specific object");
    }
  };
};

export const removePropertyFromTable = (tableId, propertyId) => {
  return async (dispatch, getState) => {
    try {
      const { selectedProductLine, tables } = getState().products;
      await api.removePropertyFromRuleTable(selectedProductLine.id, tableId, [
        propertyId,
      ]);

      const updatedTable = { ...tables.byId[tableId] };
      const newProperties = updatedTable.properties.filter(
        (property) => property.id !== propertyId
      );
      const newRows = updatedTable.rows.map((row) => {
        return {
          ...row,
          cells: row.cells.filter((cell) => cell.propertyId !== propertyId),
        };
      });

      updatedTable.properties = newProperties;
      updatedTable.rows = newRows;

      dispatch({
        type: SET_TABLE,
        payload: { data: updatedTable, tableId },
      });
    } catch (e) {
      toast.error(e);
      console.error(e, "error while deleting a property from a rule table");
    }
  };
};

export const getNextTablesPage = (page: number) => {
  return async (dispatch) => {
    dispatch({ type: SET_PL_TABLES_PAGINATION, payload: { page } });
  };
};

export const fetchTableSummariesAction = (
  filters?: GetTableSummariesRequest
): AppThunk => {
  return async (dispatch, getState) => {
    const productLineId = getState().products?.selectedProductLine?.id;
    if (!productLineId) return;

    dispatch({
      type: SET_TABLE_SUMMARIES,
      payload: { status: "loading" as TableAsyncStatus },
    });

    try {
      const { data } = await api.getTableSummaries(productLineId, filters);

      dispatch({
        type: SET_TABLE_SUMMARIES,
        payload: { status: "loaded" as TableAsyncStatus, data },
      });
    } catch (e) {
      console.error(e, "error while fetching table summaries");
      toast.error("Failed to load table summaries");
      dispatch({
        type: SET_TABLE_SUMMARIES,
        payload: { status: "error" as TableAsyncStatus },
      });
    }
  };
};

export const fetchTableIfNeededAction = (tableId: string) => {
  return async (dispatch, getState) => {
    const productLineId = getState().products?.selectedProductLine?.id;
    if (!productLineId) return;

    const { tables } = getState().products;

    if (tables.byId[tableId]) {
      if (!tables.allIds.includes(tableId)) {
        dispatch({
          type: SET_TABLE,
          payload: { data: tables.byId[tableId], tableId },
        });
      } else if (tables.tableLoadStatus[tableId] !== "loaded") {
        dispatch({
          type: SET_TABLE_LOAD_STATUS,
          payload: { tableId, status: "loaded" as TableAsyncStatus },
        });
      }
      return;
    }

    if (tables.tableLoadStatus[tableId] === "loading") {
      return;
    }

    dispatch({
      type: SET_TABLE_LOAD_STATUS,
      payload: { tableId, status: "loading" as TableAsyncStatus },
    });

    try {
      const { data } = await api.getRuleTable(productLineId, tableId);

      dispatch({
        type: SET_TABLE,
        payload: { data, tableId: data.id },
      });
      dispatch({
        type: SET_TABLE_LOAD_STATUS,
        payload: { tableId, status: "loaded" as TableAsyncStatus },
      });
    } catch (e) {
      console.error(e, `error while loading table ${tableId}`);
      toast.error("Failed to load rule table");
      dispatch({
        type: SET_TABLE_LOAD_STATUS,
        payload: { tableId, status: "error" as TableAsyncStatus },
      });
    }
  };
};

export const setTablesInitialState = () => {
  return {
    type: SET_TABLES_INITIAL_STATE,
  };
};

/** Drop full table payloads / load status so the next open refetches.
 * Omit `tableIds` (or pass undefined) to clear the entire table cache. */
export const clearCachedTablesAction = (tableIds?: readonly string[]) => {
  return {
    type: CLEAR_CACHED_TABLES,
    payload: { tableIds },
  };
};
